import { getRecaptchaToken } from "../../utils/recaptcha";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  attachRun,
  deleteUpload,
  startRun,
  uploadFile,
  type StreamEvent,
  type Upload,
} from "./streamClient";

export type Attachment = { id: string; filename: string; sizeBytes: number };

/** A file the agent generated, offered back as a download. */
export type Artifact = { url: string; filename: string; rows: number };

export type ChatMessage = {
  id: string;
  role: "user" | "assistant";
  text: string;
  /** Files this message carried, kept so the transcript still shows them. */
  attachments?: Attachment[];
  /** Tool activity surfaced under the message while the agent works. */
  activity?: { label: string; done: boolean }[];
  /** Files produced during this turn. */
  artifacts?: Artifact[];
  /** A drafted note to the team, awaiting the visitor's send. */
  callRequest?: { draft: string; email: string };
};

const newId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const toAttachment = (upload: Upload): Attachment => ({
  id: upload.id,
  filename: upload.filename,
  sizeBytes: upload.size_bytes,
});

/**
 * Drives one agent conversation, including its attachments.
 *
 * Files upload as soon as they are picked rather than on send, so a large deck
 * converts and transfers while the visitor is still typing. They stay "pending"
 * server-side until a message binds them, which is what lets one be removed
 * again before it is sent.
 *
 * The session id can be created by either path - a visitor's first action may
 * be an upload rather than a message - so it is owned here and both callers
 * read and write it.
 */
/** Everything the panel needs to render and drive a conversation. */
export type AgentChat = ReturnType<typeof useAgentChat>;

export const useAgentChat = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The unsent composer text. Held here rather than in the panel so that a
  // page change does not discard something half-typed.
  const [draft, setDraft] = useState("");
  const sessionIdRef = useRef<string | null>(null);
  // Held in memory only, never in localStorage: it is the proof of ownership
  // for this conversation, and persisting it would outlive the tab it belongs
  // to and survive on a shared machine.
  const sessionTokenRef = useRef<string | null>(null);
  const lastSeqRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => () => abortRef.current?.abort(), []);

  const addFiles = useCallback(async (files: File[]) => {
    if (files.length === 0) return;
    setError(null);
    setUploadingCount((n) => n + files.length);

    // Sequential rather than parallel: each upload may trigger a LibreOffice
    // conversion on the server, and a visitor dropping ten documents at once
    // should not start ten of those simultaneously.
    for (const file of files) {
      try {
        const upload = await uploadFile(
          file,
          sessionIdRef.current,
          undefined,
          sessionTokenRef.current
        );
        sessionIdRef.current = upload.session_id;
        if (upload.session_token) sessionTokenRef.current = upload.session_token;
        setAttachments((prev) => [...prev, toAttachment(upload)]);
      } catch (e) {
        setError(
          e instanceof Error ? e.message : `Could not upload ${file.name}.`
        );
      } finally {
        setUploadingCount((n) => Math.max(0, n - 1));
      }
    }
  }, []);

  const removeAttachment = useCallback(async (id: string) => {
    // Dropped from the composer first so the click feels immediate; the server
    // call is cleanup, and a failed one only leaves an unreferenced file.
    setAttachments((prev) => prev.filter((a) => a.id !== id));
    if (sessionIdRef.current) {
      try {
        await deleteUpload(id, sessionIdRef.current, sessionTokenRef.current);
      } catch {
        /* the file is already out of the composer and will never be bound */
      }
    }
  }, []);

  const send = useCallback(
    async (input: string) => {
      const text = input.trim();
      const attached = attachments;
      if ((!text && attached.length === 0) || isStreaming) return;

      setError(null);
      setIsStreaming(true);
      setAttachments([]);

      const assistantId = newId();
      setMessages((prev) => [
        ...prev,
        {
          id: newId(),
          role: "user",
          text,
          attachments: attached.length ? attached : undefined,
        },
        { id: assistantId, role: "assistant", text: "", activity: [] },
      ]);

      const controller = new AbortController();
      abortRef.current = controller;

      const apply = (event: StreamEvent) => {
        if ("seq" in event) lastSeqRef.current = event.seq;

        setMessages((prev) =>
          prev.map((m) => {
            if (m.id !== assistantId) return m;
            switch (event.type) {
              case "token":
                return { ...m, text: m.text + event.text };
              case "message":
                return { ...m, text: event.text };
              case "replace":
                // Everything streamed so far is discarded, along with any
                // in-progress tool activity - the reply is being withdrawn,
                // not appended to.
                return {
                  ...m,
                  text: event.text,
                  activity: [],
                  artifacts: [],
                  callRequest: undefined,
                };
              case "tool_start":
                return {
                  ...m,
                  activity: [
                    ...(m.activity ?? []),
                    { label: event.label, done: false },
                  ],
                };
              case "call_request":
                return {
                  ...m,
                  callRequest: { draft: event.draft, email: event.email },
                };
              case "artifact":
                return {
                  ...m,
                  artifacts: [
                    ...(m.artifacts ?? []),
                    { url: event.url, filename: event.filename, rows: event.rows },
                  ],
                };
              case "tool_end":
                return {
                  ...m,
                  activity: (m.activity ?? []).map((a, i, arr) =>
                    i === arr.length - 1 ? { ...a, done: true } : a
                  ),
                };
              default:
                return m;
            }
          })
        );
      };

      try {
        // Only the first turn is scored; later turns carry the session
        // token instead, so a long conversation never stalls on an expired
        // captcha token.
        const recaptchaToken = sessionIdRef.current
          ? null
          : await getRecaptchaToken("chat");

        const { run_id, session_id, session_token } = await startRun(
          text,
          sessionIdRef.current,
          attached.map((a) => a.id),
          controller.signal,
          sessionTokenRef.current,
          recaptchaToken
        );
        sessionIdRef.current = session_id;
        if (session_token) sessionTokenRef.current = session_token;
        lastSeqRef.current = 0;

        for await (const event of attachRun(
          run_id,
          lastSeqRef.current,
          controller.signal
        )) {
          if (event.type === "error") {
            setError(event.message);
            break;
          }
          if (event.type === "done") break;
          apply(event);
        }
      } catch (e) {
        // An abort is a deliberate teardown, not a failure to report.
        if ((e as Error).name !== "AbortError") {
          setError(
            e instanceof Error ? e.message : "The agent is unavailable right now."
          );
        }
      } finally {
        setIsStreaming(false);
        abortRef.current = null;
      }
    },
    [attachments, isStreaming]
  );

  const stop = useCallback(() => {
    abortRef.current?.abort();
    setIsStreaming(false);
  }, []);

  return {
    draft,
    setDraft,
    sessionId: sessionIdRef.current,
    sessionToken: sessionTokenRef.current,
    messages,
    attachments,
    isUploading: uploadingCount > 0,
    isStreaming,
    error,
    addFiles,
    removeAttachment,
    send,
    stop,
  };
};
